import { ErrorEvent, EventHint } from '@sentry/core';

/*
 * The last gate before an event leaves the browser, applied in Sentry's `beforeSend`.
 *
 * `GQLProvider`'s error link never reports a 401 itself, but the `ApolloError` it leaves behind
 * still reaches Sentry from wherever the failed operation surfaces — a component's `catch`,
 * an unhandled rejection, the error boundary. This module sees every event whatever its origin,
 * so it is the one place that can drop them all.
 */

const UNAUTHORIZED_STATUS_CODE = 401;

/** Apollo throws a `ServerError` with the first message for every operation that gets a 401. */
const UNAUTHORIZED_MESSAGE_PATTERNS: RegExp[] = [/received status code 401/i, /\b401\b[\s:(]*unauthorized/i];

const STATUS_CODE_KEYS = ['statusCode', 'status'] as const;
const NESTED_ERROR_KEYS = ['networkError', 'cause', 'response', 'originalError'] as const;
const MAX_NESTING_DEPTH = 3;

const matchesUnauthorizedMessage = (value: unknown): boolean =>
  typeof value === 'string' && UNAUTHORIZED_MESSAGE_PATTERNS.some((pattern) => pattern.test(value));

const isUnauthorizedErrorObject = (value: unknown, depth: number): boolean => {
  if (!value || typeof value !== 'object' || depth > MAX_NESTING_DEPTH) {
    return false;
  }

  const record = value as Record<string, unknown>;

  if (STATUS_CODE_KEYS.some((key) => record[key] === UNAUTHORIZED_STATUS_CODE)) {
    return true;
  }

  if (matchesUnauthorizedMessage(record.message)) {
    return true;
  }

  return NESTED_ERROR_KEYS.some((key) => isUnauthorizedErrorObject(record[key], depth + 1));
};

/** `true` for anything caused by an expired session — a 401 response however deeply it is wrapped. */
export const isUnauthorizedError = (error: unknown): boolean =>
  matchesUnauthorizedMessage(error) || isUnauthorizedErrorObject(error, 0);

/**
 * A 401 is not a bug: the session expired and every in-flight operation fails at once,
 * so the whole burst is dropped instead of flooding Sentry with unactionable events.
 */
export const isUnauthorizedSentryEvent = (event: ErrorEvent, hint?: EventHint): boolean => {
  if (isUnauthorizedError(hint?.originalException)) {
    return true;
  }

  if (matchesUnauthorizedMessage(event.message)) {
    return true;
  }

  return Boolean(event.exception?.values?.some(({ type, value }) => [type, value].some(matchesUnauthorizedMessage)));
};

/**
 * `GQLProvider`'s error link already handled every GraphQL error: it notified the user and either
 * reported it or deliberately skipped it via `isExpectedGQLError`. The `ApolloError` the operation
 * then rejects with is a duplicate of that — usually an unhandled rejection of a backend validation
 * error the user has already seen. Network errors are left alone, the link does not report all of them.
 */
export const isHandledGraphQLError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') {
    return false;
  }

  const { name, graphQLErrors, networkError } = error as Record<string, unknown>;

  return name === 'ApolloError' && Array.isArray(graphQLErrors) && graphQLErrors.length > 0 && !networkError;
};

/**
 * A request that got no response at all — offline, a dropped connection, a page unloading mid-request —
 * rejects `fetch` with a `TypeError` ("Failed to fetch", WebKit's "Load failed"). `GQLProvider`'s error
 * link shows the user a network error for it; the `ApolloError` the operation then rejects with, often
 * unhandled at the call site, is the user's connection, not something in this app to fix.
 * A network error with an HTTP status is a `ServerError`, not a `TypeError`, and is left alone.
 */
export const isHandledNetworkError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') {
    return false;
  }

  const { name, networkError } = error as Record<string, unknown>;

  return name === 'ApolloError' && networkError instanceof TypeError;
};

/** Every chunk of this app — modern or legacy — is served as a script file from `/assets/`. */
const SCRIPT_FILE_PATTERN = /\.m?js(?:[?#]|$)/i;

/**
 * An error whose stack never passes through a script file was thrown by code the browser injected
 * into the page, not by this app. Chrome on iOS (WKWebView) evaluates its translate / autofill
 * helpers as page scripts, so their frames carry the page URL itself — e.g. `/offers:228:409`,
 * beyond the end of the document — and a runaway recursion there lands in Sentry as a
 * `RangeError: Maximum call stack size exceeded` nobody can act on.
 * An event without any stack frames is kept: there is nothing to tell where it came from.
 */
export const isBrowserInjectedErrorEvent = (event: ErrorEvent): boolean => {
  const frames = event.exception?.values?.flatMap(({ stacktrace }) => stacktrace?.frames ?? []) ?? [];

  return frames.length > 0 && frames.every(({ filename }) => !filename || !SCRIPT_FILE_PATTERN.test(filename));
};

/** The HOOPS engine `@xometry/hoops-viewer` loads from the static host, e.g. `/public/hoops/monolithic_2024_8_0.js`. */
const HOOPS_ENGINE_SCRIPT_PATTERN = /\/hoops\/monolithic_[\d_]+\.js(?:[?#]|$)/;

/** V8's wording when the engine names the program `createProgram()` handed back as `null`. */
const LOST_WEBGL_CONTEXT_MESSAGE = "Cannot set properties of null (setting 'name')";

/**
 * The 3D viewer's engine crashed because the browser took its WebGL context away — a GPU process crash or reset, an
 * old driver. Every WebGL factory returns `null` on a lost context, and the engine, still starting up, sets the name of
 * the `null` it got from `createProgram()` out of its own `requestAnimationFrame` loop. Nothing in this app can catch
 * that or bring the context back; it is the user's machine. Only that message, thrown by the engine itself, is matched,
 * so any other engine error still reports.
 */
export const isHoopsLostWebGLContextErrorEvent = (event: ErrorEvent): boolean =>
  event.exception?.values?.some(({ value, stacktrace }) => {
    const frames = stacktrace?.frames ?? [];
    // Not `frames.at(-1)`: this runs in every browser that reports, and Safari < 15.4 has no `Array.prototype.at`.
    const thrower = frames[frames.length - 1]?.filename;

    return value === LOST_WEBGL_CONTEXT_MESSAGE && Boolean(thrower && HOOPS_ENGINE_SCRIPT_PATTERN.test(thrower));
  }) ?? false;

/**
 * User-Agent strings no real browser sends. `pc` is what Baiduspider's page renderer reports; it breaks its own
 * `addEventListener` wrapper (`addEL_hook`) on the sign-in and sign-up pages and files that as our error.
 */
const BOGUS_USER_AGENTS: ReadonlySet<string> = new Set(['pc']);

/**
 * Sentry's `HttpContext` integration copies `navigator.userAgent` into the event's request headers before
 * `beforeSend` runs. A crawler whose headless browser announces itself with a made-up User-Agent is not a user,
 * so nothing it breaks is ours to fix; a real browser never sends one of these strings.
 */
export const isBogusUserAgentEvent = (event: ErrorEvent): boolean => {
  const userAgent = event.request?.headers?.['User-Agent'];

  return userAgent !== undefined && BOGUS_USER_AGENTS.has(userAgent);
};
